import { Badge, Currency } from "@xola/ui-kit";
import React from "react";
import {
    ExperiencePurchaseItem,
    LineItem,
    Payment,
    PaymentLineItemStatus,
    PaymentMethod,
    Purchase,
    PurchaseItem,
    Traveler,
} from "types";
import { useSeller } from "../../../hooks/useAccount";
import { useResource } from "../../../hooks/useResource";
import { Loader } from "../../Loader";
import { SplitPayGuestMenu } from "./SplitPayGuestMenu";
import { NoTranslate } from "@/components/Common/NoTranslate";

interface PurchasePayersProps {
    purchase: Purchase;
    purchaseItem: PurchaseItem;
}

export const PurchasePayers = ({ purchase, purchaseItem }: PurchasePayersProps) => {
    const splitPayLineItems = purchase.lineItems.filter(({ traveler, payment, status }) => {
        const isPayment = traveler?.id && payment?.method !== PaymentMethod.LATER;
        const isValid = status === PaymentLineItemStatus.COMPLETE || status === PaymentLineItemStatus.PENDING;

        return isValid && isPayment;
    });
    const groupedLineItems: Record<string, { travelerId?: string; amount: number; status: string; payment?: Payment }> =
        {};

    const payerTravelerIds = splitPayLineItems
        .filter((lineItem) => !!lineItem.traveler)
        .map((lineItem) => lineItem.traveler?.id);

    if (purchase.splitPayment?.enabled && purchase.organizer && !payerTravelerIds.includes(purchase.organizer.id)) {
        // show the organizer with $0 payment even if they haven't made a payment, so that we have an option to resend split pay email
        // Refer to X2-8385
        groupedLineItems[purchase.organizer.id] = {
            travelerId: purchase.organizer.id,
            amount: 0,
            status: PaymentLineItemStatus.COMPLETE,
        };
    }

    for (const lineItem of splitPayLineItems) {
        const travelerId = lineItem.traveler?.id;

        const key = travelerId ? travelerId : lineItem.id;
        if (groupedLineItems[key]) {
            groupedLineItems[key].amount += lineItem.amount;
        } else {
            groupedLineItems[key] = {
                travelerId,
                amount: lineItem.amount,
                payment: lineItem.payment,
                status: lineItem.status,
            };
        }
    }

    return (
        <div className="flex flex-col space-y-2 divide-y divide-gray-light border-b border-gray-light pb-1">
            {Object.entries(groupedLineItems).map(([key, groupedLineItem]) => (
                <PayerItem
                    key={key}
                    payerItemData={{
                        traveler: groupedLineItem?.travelerId ? { id: groupedLineItem?.travelerId } : undefined,
                        amount: groupedLineItem.amount,
                        payment: groupedLineItem.payment,
                        status: groupedLineItem.status,
                    }}
                    purchase={purchase}
                    purchaseItem={purchaseItem}
                />
            ))}
        </div>
    );
};

const PayerItem = ({
    payerItemData,
    purchase,
    purchaseItem,
}: {
    payerItemData: Pick<LineItem, "traveler" | "amount" | "payment" | "status">; // Can either be proper lineItem or merged lineItems data based on traveler.id
    purchase: Purchase;
    purchaseItem: PurchaseItem;
}) => {
    const seller = useSeller();
    const { data: traveler, isLoading } = useResource<Traveler>(["users", payerItemData.traveler?.id], {
        private: true,
    });

    if (isLoading) {
        return <Loader />;
    }

    let amount = payerItemData.traveler?.id ? Math.abs(payerItemData.amount) : Math.abs(purchase.balance);
    if (payerItemData.status === PaymentLineItemStatus.PENDING) {
        amount = 0;
    }

    return (
        <div key={traveler?.id} className="flex items-center justify-between pt-1" data-testid="purchase-payers-item">
            <NoTranslate as="div" className="font-bold">
                {traveler?.name}
            </NoTranslate>
            <div className="flex items-center space-x-2">
                {payerItemData.payment?.meta?.cardSummary?.last4 ? (
                    <Badge color="secondary" size="small" data-testid="purchase-payers-item-card">
                        <span>*{payerItemData.payment.meta.cardSummary.last4}</span>
                    </Badge>
                ) : null}
                <Badge color="secondary" size="small" data-testid="purchase-payers-item-amount">
                    <Currency currency={seller.currency}>{amount}</Currency>
                </Badge>
                <SplitPayGuestMenu
                    traveler={traveler}
                    purchase={purchase}
                    purchaseItem={purchaseItem as ExperiencePurchaseItem}
                />
            </div>
        </div>
    );
};
